import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import {
  WORKSINE_APPS_CHANGED_EVENT,
  getWorksineAppInstallations,
  getWorksineAppsCatalog
} from "@/api/worksine-apps/actions";
import type { WorksineAppManifest, WorksineAppSurface } from "@/api/worksine-apps/types";
import { getSidebarMenus } from "@/configs/menus";
import { PROTECTED_ROUTES } from "@/configs/protectedRoutes";
import { PATH } from "@/configs/router/routerConfig";
import { Menu } from "@/contexts/SidebarContext";
import { PRODUCT_PATHS, type ProductRouteKey } from "@/lib/platform/productHandoff";
import { RootState } from "@/store";
import { useTranslation } from "next-i18next";

const SAFE_QUERY_KEYS = new Set(["tenant", "tenant_id", "workspace", "workspace_id", "redirect", "return_url"]);

const safeManifestPath = (value: string): boolean => {
  if (
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    Array.from(value).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
  )
    return false;
  try {
    const parsed = new URL(value, "https://worksine.invalid");
    return (
      !parsed.hash && !Array.from(parsed.searchParams.keys()).some((key) => SAFE_QUERY_KEYS.has(key.toLowerCase()))
    );
  } catch {
    return false;
  }
};

const isProtectedManifestPath = (value: string): boolean =>
  PROTECTED_ROUTES.some(
    (route) => value === route.path || (route.exact === false && value.startsWith(`${route.path}/`))
  );

const dynamicSurfaceMenu = (app: WorksineAppManifest, surface: WorksineAppSurface): Menu | null => {
  if (!["apps", "settings", "integrations"].includes(surface.placement)) return null;
  const productRouteKey = surface.product_route_key;
  if (productRouteKey && !Object.prototype.hasOwnProperty.call(PRODUCT_PATHS, productRouteKey)) return null;
  if (!productRouteKey && (!safeManifestPath(surface.path) || !isProtectedManifestPath(surface.path))) return null;
  const path = productRouteKey ? PRODUCT_PATHS[productRouteKey as ProductRouteKey] : surface.path;
  const children = surface.children
    ?.map((child) => dynamicSurfaceMenu(app, child))
    .filter((child): child is Menu => Boolean(child));
  return {
    appId: app.app_id,
    path,
    title: surface.title,
    icon: surface.icon || "Apps",
    permissions: surface.permissions as Menu["permissions"],
    applicationCapabilities: surface.application_capabilities,
    productRouteKey,
    breadcrumb: { title: surface.title },
    ...(children?.length ? { subMenu: children } : {})
  };
};

export const buildDynamicMenus = (
  catalog: { apps: WorksineAppManifest[] },
  installations: { app_id: string; manifest_digest: string; status: string }[]
): { apps: Menu[]; settings: Menu[]; integrations: Menu[] } => {
  const result = { apps: [], settings: [], integrations: [] } as {
    apps: Menu[];
    settings: Menu[];
    integrations: Menu[];
  };
  const approved = new Map(
    installations
      .filter((installation) => installation.status === "approved")
      .map((installation) => [installation.app_id, installation.manifest_digest])
  );
  for (const app of catalog.apps) {
    if (approved.get(app.app_id) !== app.manifest_digest) continue;
    const settingsForApp: Menu[] = [];
    for (const surface of app.surfaces) {
      const menu = dynamicSurfaceMenu(app, surface);
      if (!menu) continue;
      if (surface.placement === "settings") settingsForApp.push(menu);
      else result[surface.placement].push(menu);
    }
    if (settingsForApp.length) {
      result.settings.push({ appId: app.app_id, title: app.display_name, icon: "Apps", subMenu: settingsForApp });
    }
  }
  return result;
};

const appendUnique = (menus: Menu[], additions: Menu[]): Menu[] => {
  const existingPaths = new Set<string>();
  const collectPaths = (items: Menu[]) => {
    for (const item of items) {
      if (item.path) existingPaths.add(item.path);
      if (item.subMenu) collectPaths(item.subMenu);
    }
  };
  collectPaths(menus);
  const filterAddition = (menu: Menu, ancestorPaths: Set<string> = new Set()): Menu | null => {
    if (menu.path && existingPaths.has(menu.path) && !ancestorPaths.has(menu.path)) return null;
    const nextAncestorPaths = new Set(ancestorPaths);
    if (menu.path) nextAncestorPaths.add(menu.path);
    const children = menu.subMenu
      ?.map((child) => filterAddition(child, nextAncestorPaths))
      .filter((child): child is Menu => Boolean(child));
    if (menu.subMenu && !children?.length) return null;
    if (menu.path) existingPaths.add(menu.path);
    return children ? { ...menu, subMenu: children } : menu;
  };
  return [...menus, ...additions.map((menu) => filterAddition(menu)).filter((menu): menu is Menu => Boolean(menu))];
};

export const mergeDynamicMenus = (
  menus: Menu[],
  dynamic: { apps: Menu[]; settings: Menu[]; integrations: Menu[] }
): Menu[] =>
  menus.map((menu) => {
    if (!menu.path && menu.icon === "Apps") return { ...menu, subMenu: appendUnique(menu.subMenu || [], dynamic.apps) };
    if (menu.path === PATH.SETTING) {
      return {
        ...menu,
        subMenu: (menu.subMenu || []).map((child) => {
          if (!child.path && child.icon === "Apps")
            return { ...child, subMenu: appendUnique(child.subMenu || [], dynamic.settings) };
          if (child.path === PATH.SYSTEM_INTEGRATIONS)
            return { ...child, subMenu: appendUnique(child.subMenu || [], dynamic.integrations) };
          return child;
        })
      };
    }
    return menu;
  });

export const shouldApplyDynamicMenuRefresh = (requestGeneration: number, currentGeneration: number): boolean =>
  requestGeneration === currentGeneration;

export function useSidebarMenus(): Menu[] {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const isSandboxTenant = user?.isSandboxTenant;
  const tenantGuid = user?.tenantGuid || "";
  const [dynamicMenus, setDynamicMenus] = useState<{ apps: Menu[]; settings: Menu[]; integrations: Menu[] }>({
    apps: [],
    settings: [],
    integrations: []
  });
  const refreshGeneration = useRef(0);

  useEffect(() => {
    let active = true;
    setDynamicMenus({ apps: [], settings: [], integrations: [] });
    if (!tenantGuid)
      return () => {
        active = false;
      };
    const refresh = () => {
      const generation = ++refreshGeneration.current;
      setDynamicMenus({ apps: [], settings: [], integrations: [] });
      void Promise.all([getWorksineAppsCatalog(), getWorksineAppInstallations()])
        .then(([catalog, installationResponse]) => {
          if (active && shouldApplyDynamicMenuRefresh(generation, refreshGeneration.current))
            setDynamicMenus(buildDynamicMenus(catalog, installationResponse.installations));
        })
        .catch(() => undefined);
    };
    refresh();
    window.addEventListener(WORKSINE_APPS_CHANGED_EVENT, refresh);
    return () => {
      active = false;
      refreshGeneration.current += 1;
      window.removeEventListener(WORKSINE_APPS_CHANGED_EVENT, refresh);
    };
  }, [tenantGuid]);

  const menus = useMemo(() => {
    const legacyMenus = getSidebarMenus(t, isSandboxTenant);
    return mergeDynamicMenus(legacyMenus, dynamicMenus);
  }, [dynamicMenus, isSandboxTenant, t]);

  return menus;
}
